import { strategicPlanningApi } from "@/api/strategicPlanningApi";
import KpiBasicChartCard from "@/components/KpiBasicChartCard";
import Loading from "@/components/feedback/Loading";
import StrategicPlanStatus from "@/components/plan/StrategicPlanStatus";
import { StrategicPlanningActionPlanIcon, StrategicPlanningGoalIcon, StrategicPlanningObjectiveIcon } from "@/config";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Avatar,
    Box,
    Card,
    CardActionArea,
    CardContent,
    CardHeader,
    Collapse,
    Container,
    Divider,
    Grid,
    IconButton,
    Stack,
    Tab,
    Tabs,
} from "@mui/material";
import { Suspense, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import DashboardHeader from "../DashboardHeader";

const useStyles = {
    title: {
        variant: "body1",
        fontWeight: "bold",
        // color: "#00008B",
        color: "primary",
    },
    subtitle: {
        variant: "subtitle1",
        // color: "GrayText",
        // verticalAlign: "middle",
        // display: "inline-flex",
    },
};

function TabPanel(props) {
    const { children, currentTab, value, ...other } = props;

    return <Box hidden={currentTab !== value}>{currentTab === value && children}</Box>;
}

function KpiGraphContainer(props) {
    const { kpis } = props;

    return (
        <Grid container spacing={1} alignItems="stretch">
            {kpis?.map((kpi, index) => (
                <Grid key={index} item md={6} sm={12}>
                    <KpiBasicChartCard kpiId={kpi.id} />
                </Grid>
            ))}
        </Grid>
    );
}

function ActionPlanSection(props) {
    const [open, setOpen] = useState(false);
    const { actionPlan } = props;
    const { code, title } = actionPlan;
    const kpis = actionPlan?.kpis;

    const avatar = () => {
        return (
            <Avatar>
                <StrategicPlanningActionPlanIcon />
            </Avatar>
        );
    };
    const actionMenu = () => {
        return open ? <ExpandLessIcon /> : <ExpandMoreIcon />;
    };

    return (
        <Card>
            <CardActionArea onClick={() => setOpen((prev) => !prev)}>
                <CardHeader
                    avatar={avatar()}
                    title={`${code} ${title}`}
                    titleTypographyProps={{ ...useStyles.title }}
                    subheader="แผนปฏิบัติการ"
                    subheaderTypographyProps={{ ...useStyles.subtitle }}
                    action={actionMenu()}
                ></CardHeader>
            </CardActionArea>
            <Collapse in={open} timeout="auto">
                <CardContent>
                    <KpiGraphContainer kpis={kpis} />
                </CardContent>
            </Collapse>
        </Card>
    );
}

function GoalSection(props) {
    const [open, setOpen] = useState(false);
    const { goal } = props;
    const { code, title } = goal;
    const kpis = goal?.kpis;
    const avatar = () => {
        return (
            <Avatar>
                <StrategicPlanningGoalIcon />
            </Avatar>
        );
    };
    const actionMenu = () => {
        return open ? <ExpandLessIcon /> : <ExpandMoreIcon />;
    };

    return (
        <Card>
            <CardActionArea onClick={() => setOpen((prev) => !prev)}>
                <CardHeader
                    avatar={avatar()}
                    title={`${code} ${title}`}
                    titleTypographyProps={{ ...useStyles.title }}
                    subheader="เป้าประสงค์"
                    subheaderTypographyProps={{ ...useStyles.subtitle }}
                    action={actionMenu()}
                ></CardHeader>
            </CardActionArea>
            <Collapse in={open} timeout="auto">
                <CardContent>
                    <KpiGraphContainer kpis={kpis} />
                    <Stack spacing={0.5} sx={{ marginTop: 3 }}>
                        {goal?.action_plans?.map((actionPlan, index) => (
                            <ActionPlanSection key={index} actionPlan={actionPlan} />
                        ))}
                    </Stack>
                </CardContent>
            </Collapse>
        </Card>
    );
}

function ObjectiveSection(props) {
    const { objective } = props;
    const { code, title } = objective;
    const avatar = () => {
        return (
            <Avatar>
                <StrategicPlanningObjectiveIcon />
            </Avatar>
        );
    };

    return (
        <Card>
            <CardHeader
                avatar={avatar()}
                title={`${code} ${title}`}
                titleTypographyProps={{ ...useStyles.title }}
                subheader="วัตถุประสงค์เชิงกลยุทธ์"
                subheaderTypographyProps={{ ...useStyles.subtitle }}
            ></CardHeader>
            <CardContent>
                <Stack spacing={0.5}>
                    {objective?.goals?.map((goal, index) => <GoalSection key={index} goal={goal} />)}
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function StrategicPlanningDashboard(props) {
    const [currentTab, setCurrentTab] = useState(0);
    const { dashboard } = useOutletContext();
    const planId = dashboard?.plan_id;
    const getPlan = strategicPlanningApi.endpoints.getPlanById.useQuery({
        id: planId,
    });
    const isLoading = getPlan.isLoading;
    const isError = getPlan.isError;
    const plan = getPlan.data?.data;
    const navigate = useNavigate();
    const handleChangeTab = (event, newTab) => setCurrentTab(newTab);

    if (isLoading) {
        return <Loading />;
    }
    if (isError) {
        return `ERROR - cannot load strategic planning id: ${planId}`;
    }

    return (
        <>
            <Container>
                <DashboardHeader
                    title={`${plan?.title}`}
                    titleTypographyProps={{ ...useStyles.title }}
                    subtitle={`${plan?.description}`}
                    subheaderTypographyProps={{ ...useStyles.subtitle }}
                />
                <StrategicPlanStatus strategicPlanning={plan} />
                <Box
                    sx={{
                        marginTop: 2,
                        borderBottom: 1,
                        borderColor: "divider",
                    }}
                >
                    <Tabs value={currentTab} onChange={handleChangeTab} sx={{}}>
                        {plan?.objectives?.map((objective, index) => (
                            <Tab key={index} value={index} label={objective.code} />
                        ))}
                    </Tabs>
                </Box>
            </Container>
            <Container sx={{ marginTop: 3 }}>
                <Stack spacing={0.5}>
                    <Suspense fallback="loading...">
                        {plan?.objectives?.map((objective, index) => (
                            <TabPanel key={index} currentTab={currentTab} value={index}>
                                <ObjectiveSection objective={objective} />
                            </TabPanel>
                        ))}
                    </Suspense>
                </Stack>
            </Container>
        </>
    );
}
